import { useMemo, useState } from "react";
import { useMutation, useQuery } from "@apollo/client";

import Button from "@/components/Button";
import Progress from "@/components/Progress";
import Select from "@/components/Select";
import TextField from "@/components/TextField";
import useModal from "@/hooks/useModal";

import { CHANGE_REQUESTS_QUERY, Node, REASSIGN_CHANGE_REQUEST_MUTATION, REASSIGN_SECTIONS_QUERY } from "../queries";

type Props = {
  changeRequest: Node;
  onClosed(): void;
};

export default function ReassignModal({ changeRequest, onClosed }: Props) {
  const { Modal, save } = useModal();
  const [targetSectionId, setTargetSectionId] = useState("");
  const [message, setMessage] = useState("");

  const sourceSectionId = changeRequest.section.id;
  const { data, loading: sectionsLoading } = useQuery(REASSIGN_SECTIONS_QUERY);
  const [reassign, { loading: reassigning }] = useMutation(REASSIGN_CHANGE_REQUEST_MUTATION, {
    refetchQueries: [CHANGE_REQUESTS_QUERY],
    awaitRefetchQueries: true
  });

  const options = useMemo(() => {
    const sections = data?.sections ?? [];
    return sections
      .filter((section) => section.id !== sourceSectionId)
      .map((section) => ({ value: section.id, displayText: section.name }))
      .sort((a, b) => a.displayText.localeCompare(b.displayText));
  }, [data, sourceSectionId]);

  const onSave = () =>
    save(
      () =>
        reassign({
          variables: {
            id: changeRequest.id,
            targetSectionId,
            message: message.trim() || undefined
          }
        }),
      (data) => data.reassignChangeRequest,
      "Change request reassigned"
    );

  return (
    <Modal
      displayOverflow
      onClosed={onClosed}
      headline="Reassign Change Request"
      action={
        <Button disabled={!targetSectionId || reassigning} onClick={onSave} icon="swap_horiz">
          Reassign
        </Button>
      }
    >
      {sectionsLoading ? (
        <Progress variant="linear" />
      ) : (
        <div className="flex flex-col gap-6">
          <p className="max-w-sm">
            Move this change request to a different section's queue. The new section will be notified.
          </p>

          <Select
            label="New Section"
            className="w-full"
            options={options}
            value={targetSectionId}
            onSelect={setTargetSectionId}
            placeholder="Select a section"
            data-testid="reassign-section-select"
          />

          <TextField
            className="w-full h-28"
            variant="outlined"
            type="textarea"
            label="Reason (Optional)"
            value={message}
            onChangeText={setMessage}
            data-testid="reassign-reason"
          />
        </div>
      )}
    </Modal>
  );
}
